import '../../demand-editor/components/doc-tile.css';

import { Badge } from '@kerfjs/ui/badge';
import { ContentItem } from '@kerfjs/ui/content-item';
import { px, uiColor } from '@kerfjs/ui/css-values';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { Grid } from '@kerfjs/ui/grid';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';
import { FileText } from 'lucide';

import type { BackgroundDocT } from '../../../data/api.js';
import { selectedLibraryDocIds } from '../../../data/state.js';

export function LibraryDocuments({ docs, query = '' }: { docs: readonly BackgroundDocT[]; query?: string }): SafeHtml {
  if (docs.length === 0)
    return (
      <EmptyState
        title={query.trim() === '' ? 'No documents yet' : 'No matching documents'}
        detail={query.trim() === '' ? 'Attach a document or drop one here.' : 'Try another search.'}
      />
    );
  const selected = selectedLibraryDocIds.value;
  // Multi-select Kerf ContentItem tiles like Demand › Documents (HS2-KRH1MW): click
  // selects (Shift/Cmd extend), double-click or Enter opens, right-click and the
  // main toolbar's "…" offer the document actions. init.ts owns the gestures.
  return (
    <>
      <Grid
        minColumnWidth={px(150)}
        autoFill
        gap={{ column: 'none', row: 's' }}
        flex
        selectionMode="multiple"
        ariaLabel="Knowledge Base Documents"
        className="library-documents"
        rootAttributes={{ 'data-library-docs-grid': '' }}
      >
        {docs.map((doc) => (
          <ContentItem
            interactive
            action="library-doc-select"
            itemId={doc.id}
            selectionMode="multiple"
            selected={selected.includes(doc.id)}
            frame="framed"
            className="doc-tile library-doc-tile"
            title={doc.title ?? doc.filename}
            rootAttributes={{
              'data-key': doc.id,
              'data-library-doc-id': doc.id,
              'data-archived': doc.archived ? '' : undefined,
            }}
          >
            <div class="doc-tile-body">
              <div class="doc-icon">
                <LucideIcon icon={FileText} name="" size={30} color={uiColor('text-quiet')} />
              </div>
              <span class="doc-name">
                <Text variant="span" wrap="anywhere">
                  {doc.title ?? doc.filename}
                </Text>
              </span>
              <Text variant="span" tone="quiet" size="compact" className="doc-size">
                {new Date(doc.createdAt).toLocaleDateString()}
              </Text>
              {doc.archived || doc.versionNumber > 1 ? (
                <Badge tone="neutral" shape="rounded" size="compact" className="doc-kind">
                  {doc.archived ? 'Archived' : `Version ${String(doc.versionNumber)}`}
                </Badge>
              ) : null}
            </div>
          </ContentItem>
        ))}
      </Grid>
      <input
        class="app-visually-hidden"
        type="file"
        aria-label="Replacement Document"
        data-lib-replace-input
        hidden
        tabindex={-1}
      />
    </>
  );
}
